import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Save, CreditCard } from 'lucide-react';
import PaymentManager from '../components/PaymentManager';
import { toast } from '../hooks/use-toast';
import { findChecksMissingDate, findPaymentsMissingMethod } from '../lib/paymentValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkPayments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workId = searchParams.get('workId');
  
  const [work, setWork] = useState(null);
  const [workDetails, setWorkDetails] = useState([]);
  const [payments, setPayments] = useState([
    { id: '1', method: '', amount: '', notes: '', checkNumber: '', checkDate: '', bankName: '', branch: '', accountNumber: '' }
  ]);
  const [accountClosed, setAccountClosed] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (workId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [workId]);

  const loadData = async () => {
    try {
      // Load work
      const workRes = await fetch(`${API}/work/${workId}`);
      if (workRes.ok) {
        const workData = await workRes.json();
        setWork(workData);
      }

      // Load work details
      const detailsRes = await fetch(`${API}/work-details/${workId}`);
      if (detailsRes.ok) {
        const details = await detailsRes.json();
        setWorkDetails(details);
      }

      // Load existing payments for this work
      const paymentsRes = await fetch(`${API}/work-payments/${workId}`);
      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        if (paymentsData.length > 0) {
          setPayments(paymentsData);
          // Check if account is closed based on existing data
          const existingStatus = await fetch(`${API}/work-payment-status/${workId}`);
          if (existingStatus.ok) {
            const status = await existingStatus.json();
            setAccountClosed(status.accountClosed || false);
          }
        }
      }

      // Load bank accounts
      const bankRes = await fetch(`${API}/bank-accounts`);
      if (bankRes.ok) {
        const bankData = await bankRes.json();
        setBankAccounts(bankData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total customer price
  const calculateTotalCustomerPrice = () => {
    return workDetails.reduce((sum, item) => {
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.cost) || 0;
      // Customer price = (unitPrice * quantity) - (quantity * cost)
      // Or just unitPrice * quantity if that's the customer price
      return sum + (unitPrice * quantity);
    }, 0);
  };

  const handleSave = async () => {
    if (findPaymentsMissingMethod(payments).length > 0) {
      toast({ title: 'חסר אמצעי תשלום', description: 'מולא סכום תשלום בלי אמצעי תשלום. אם הלקוח שילם - בחר אמצעי תשלום. אם עדיין לא שילם - מחק את הסכום בשורת התשלום.', variant: 'destructive' });
      return;
    }
    if (findChecksMissingDate(payments.filter(p => p.method && p.amount)).length > 0) {
      toast({ title: 'שגיאה', description: 'חובה למלא תאריך פירעון לכל שיק', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      // Save payments
      const response = await fetch(`${API}/work-payments/${workId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments,
          accountClosed
        })
      });

      if (response.ok) {
        toast({
          title: 'נשמר בהצלחה',
          description: 'פרטי התשלום נשמרו'
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving payments:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לשמור את פרטי התשלום',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  if (!workId || !work) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex flex-col items-center justify-center gap-4" dir="rtl">
        <p className="text-xl text-red-600">לא נבחרה עבודה</p>
        <Button onClick={() => navigate('/work')}>
          <ArrowRight className="ml-2 h-4 w-4" />
          חזרה לרשימת עבודות
        </Button>
      </div>
    );
  }

  const totalAmount = calculateTotalCustomerPrice();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/work-details?workId=${workId}`)}
            className="border-orange-300 hover:bg-orange-50"
          >
            <ArrowRight className="ml-2 w-5 h-5" />
            חזרה לפרטי עבודה
          </Button>
          <h1 className="text-3xl font-bold text-orange-800">תשלומים</h1>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            <Save className="ml-2 w-5 h-5" />
            {saving ? 'שומר...' : 'שמור'}
          </Button>
        </div>

        {/* Work Info Card */}
        <Card className="mb-6 shadow-lg border-2 border-orange-200">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              תשלומים עבור: {work.workName}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-gray-500 text-sm">שם עבודה</p>
                <p className="font-bold text-lg">{work.workName}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">שם לקוח</p>
                <p className="font-bold text-lg">{work.clientName}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">תאריך</p>
                <p className="font-bold text-lg">{work.date}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">סה"כ מחיר ללקוח</p>
                <p className="font-bold text-2xl text-orange-600">₪{totalAmount.toLocaleString()}</p>
              </div>
            </div>

            {/* Work Items Summary */}
            {workDetails.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold mb-2">פריטים בעבודה:</h4>
                <div className="space-y-1">
                  {workDetails.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.partName} (x{item.quantity})</span>
                      <span className="font-medium">₪{((parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0)).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Manager */}
        <PaymentManager
          totalAmount={totalAmount}
          payments={payments}
          onPaymentsChange={setPayments}
          accountClosed={accountClosed}
          onAccountClosedChange={setAccountClosed}
          disabled={false}
          bankAccounts={bankAccounts}
        />
      </div>
    </div>
  );
};

export default WorkPayments;
